import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';

import Fader from '../Loaders/Fader';

import useOutsideClick from '../../../hooks/useOutsideClick';
import { setLoggingIn, setNotify } from '../../../datastore/actions/actionActions';

import SecurityIcon from '../../../assets/images/icons/security-dark.svg';
import RepeatIcon from '../../../assets/images/icons/repeat.svg';
import ArrowRight from '../../../assets/images/icons/arrow-right.svg';
import ArrowLeft from '../../../assets/images/icons/arrow-left.svg';
import './VerifyModal.scss';

const VerifyModal = ({
  isOpen,
  setIsOpen,
  validation,
  resend,
  setLoginChallenge,
  maskedEmail,
  maskedPhone,
  default2faMethod,
  loading,
  setLoading,
  isLogin = true,
}) => {
  const dispatch = useDispatch();

  const [code, setCode] = useState(Array(6).fill(''));
  const [timer, setTimer] = useState(60);
  const [method, setMethod] = useState(default2faMethod || 'email');

  const inputsRef = useRef([]);
  const windowRef = useRef();

  useEffect(() => {
    inputsRef.current[0].focus();
  }, []);

  useEffect(() => {
    if (timer > 0) {
      const countdown = setInterval(() => setTimer((t) => t - 1), 1000);
      return () => clearInterval(countdown);
    }
  }, [timer]);

  const handleValidation = () => {
    if (code.some((digit) => digit === '')) {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Verification code must be 6 digits long',
        }),
      );
      return;
    }

    setLoading(true);
    if (isLogin) {
      validation(code, method);
    } else {
      validation(code);
    }
  };

  const onCloseHandler = () => {
    setIsOpen(false);
    setLoginChallenge(false);

    if (isLogin) {
      dispatch(setLoggingIn(false));
    }
  };

  const handleChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    if (value && index < 5) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }

    if (e.key === 'Enter') {
      handleValidation();
    }
  };

  const handlePaste = (e, index) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('Text').replace(/\D/g, '');
    if (!pastedData) return;

    const newCode = [...code];

    for (let i = 0; i < pastedData.length; i++) {
      if (index + i < 6) {
        newCode[index + i] = pastedData[i];
      }
    }

    setCode(newCode);

    const firstEmptyIndex = newCode.findIndex((c, i) => i >= index && c === '');
    if (firstEmptyIndex !== -1) {
      inputsRef.current[firstEmptyIndex].focus();
    } else {
      inputsRef.current[5].focus();
    }
  };

  const handleResend = (method) => {
    if (isLogin) {
      resend(method);
    } else {
      resend();
    }
    setTimer(60);
    setCode(Array(6).fill(''));
    inputsRef.current[0].focus();
  };

  useOutsideClick(windowRef, null, isOpen, onCloseHandler);

  return ReactDOM.createPortal(
    <div className="login-wrap verify">
      <div className="login-view">
        <div ref={windowRef} className="login-box verify__box">
          <div className="verify__icon">
            <img src={SecurityIcon} />
          </div>
          <h2 className="verify__title">Verify Your Identity</h2>
          <p className="verify__text">
            We've sent a 6-digit verification code to your {method === 'email' ? 'email' : 'phone'}
            <strong> {method === 'email' ? maskedEmail : maskedPhone}</strong>
          </p>
          {isLogin && (
            <div className="verify__switch">
              {method === 'email' ? (
                <button
                  className="verify__resend"
                  onClick={() => {
                    setMethod('sms');
                    handleResend('sms');
                  }}
                >
                  Send via SMS
                </button>
              ) : (
                <button
                  className="verify__resend"
                  onClick={() => {
                    setMethod('email');
                    handleResend('email');
                  }}
                >
                  Send via Email
                </button>
              )}
            </div>
          )}
          <p className="verify__hint">Enter Verification Code</p>
          <div className="verify__code-inputs">
            {code.map((digit, i) => (
              <input
                key={i}
                id={`code-${i}`}
                ref={(el) => {
                  inputsRef.current[i] = el;
                }}
                type="text"
                value={digit}
                maxLength={1}
                onChange={(e) => handleChange(e.target.value, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                onPaste={(e) => handlePaste(e, i)}
              />
            ))}
          </div>

          {!loading ? (
            <>
              {timer > 0 ? (
                <p className="verify__timer">Resend code in {timer}s</p>
              ) : (
                <button className="verify__resend" onClick={() => handleResend(method)}>
                  <img src={RepeatIcon} />
                  Resend Code
                </button>
              )}
              <button className="form__btn" onClick={handleValidation}>
                Verify & Continue
                <img src={ArrowRight} />
              </button>
              <button className="form__btn" onClick={onCloseHandler}>
                <img src={ArrowLeft} />
                Back to {isLogin ? 'Login' : 'Settings'}
              </button>
            </>
          ) : (
            <div className="verify__loader">
              <Fader />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.getElementById('root'),
  );
};

export default VerifyModal;
